import request from "./../utils/request";
import log2 from "../utils/log2";

const log = log2("WEBPHONE_RS_API");

export const WEBPHONE_RS_API = {
    company: {
        /**
         * Şirket bilgilerini getirir
         * @returns {Promise<object>} - Şirket bilgileri
         */
        getInfo: () => {
            log("company.getInfo");
            return request({
                url: `/webphone/redsantral/info/company`,
                method: "get",
            }).then((response) => response.data);
        },
    },

    did: {
        /**
         * DID numaralarını listeler
         * @returns {Promise<Array>} - DID numaraları listesi
         */
        list: () => {
            log("did.list");
            return request({
                url: `/webphone/redsantral/did/list`,
                method: "get",
            }).then((response) => response.data);
        },
    },

    extensions: {
        /**
         * Dahilileri listeler
         * @param {string} extension - Belirli bir dahili araması için (isteğe bağlı)
         * @returns {Promise<Array>} - Dahili listesi
         */
        list: (extension = "") => {
            log("extensions.list", { extension });
            return request({
                url: `/webphone/redsantral/extensions/list`,
                method: "get",
                params: { extension },
            }).then((response) => response.data);
        },

        /**
         * Belirli bir dahiliyi getirir
         * @param {string} extension - Dahili numarası
         * @returns {Promise<object>} - Dahili bilgileri
         */
        get: (extension) => {
            log("extensions.get", { extension });
            return request({
                url: `/webphone/redsantral/extensions/${extension}`,
                method: "get",
            }).then((response) => response.data);
        },

        /**
         * Yeni dahili ekler
         * @param {object} extensionData - Dahili bilgileri
         * @returns {Promise<object>} - Eklenen dahili bilgileri
         */
        create: (extensionData) => {
            log("extensions.create", extensionData);
            return request.post(`/webphone/redsantral/extensions`, extensionData).then((response) => response.data);
        },

        /**
         * Dahili günceller
         * @param {string} extension - Güncellenecek dahili numarası
         * @param {object} extensionData - Güncellenecek dahili bilgileri
         * @returns {Promise<object>} - Güncelleme sonucu
         */
        update: (extension, extensionData) => {
            log("extensions.update", { extension, extensionData });
            return request
                .patch(`/webphone/redsantral/extensions/${extension}`, extensionData)
                .then((response) => response.data);
        },

        /**
         * Dahili siler
         * @param {string} extension - Silinecek dahili numarası
         * @returns {Promise<object>} - Silme işlemi sonucu
         */
        delete: (extension) => {
            log("extensions.delete", { extension });
            return request({
                url: `/webphone/redsantral/extensions/${extension}`,
                method: "delete",
            }).then((response) => response.data);
        },

        /**
         * Dahili kayıt durumunu kontrol eder
         * @param {string} extension - Kontrol edilecek dahili numarası
         * @returns {Promise<object>} - Dahili kayıt durumu
         */
        getRegisterStatus: (extension) => {
            log("extensions.getRegisterStatus", { extension });
            return request({
                url: `/webphone/redsantral/extensions/${extension}/register-status`,
                method: "get",
            }).then((response) => response.data);
        },

        /**
         * Dahili konuşma durumunu kontrol eder
         * @param {string} extension - Kontrol edilecek dahili numarası
         * @returns {Promise<object>} - Dahili konuşma durumu
         */
        getTalkStatus: (extension) => {
            log("extensions.getTalkStatus", { extension });
            return request({
                url: `/webphone/redsantral/extensions/${extension}/talk-status`,
                method: "get",
            }).then((response) => response.data);
        },

        /**
         * Dahili şifresini getirir (WebPhone için)
         * @param {string} extension - Şifresi getirilecek dahili numarası
         * @returns {Promise<object>} - Dahili şifre bilgileri
         */
        getPassword: (extension) => {
            log("extensions.getPassword", { extension });
            return request({
                url: `/webphone/redsantral/extensions/${extension}/password`,
                method: "get",
            }).then((response) => response.data);
        },
    },

    webhooks: {
        /**
         * Webhook listesini getirir
         * @returns {Promise<Array>} - Webhook listesi
         */
        list: () => {
            log("webhooks.list");
            return request({
                url: `/webphone/redsantral/webhooks`,
                method: "get",
            }).then((response) => response.data);
        },

        /**
         * Webhook ekler veya günceller
         * @param {string} responseurl - Webhook URL'i
         * @param {string} urltoken - Webhook token'ı (isteğe bağlı)
         * @returns {Promise<object>} - Webhook ekleme sonucu
         */
        set: (responseurl, urltoken = "") => {
            log("webhooks.set", { responseurl, urltoken });
            return request
                .post(`/webphone/redsantral/webhooks`, {
                    responseurl,
                    urltoken,
                })
                .then((response) => response.data);
        },

        /**
         * Webhook siler
         * @returns {Promise<object>} - Webhook silme sonucu
         */
        delete: () => {
            log("webhooks.delete");
            return request({
                url: `/webphone/redsantral/webhooks`,
                method: "delete",
            }).then((response) => response.data);
        },
    },

    reports: {
        /**
         * CDR (Call Detail Record) kayıtlarını getirir
         * @param {object} filters - Filtreleme parametreleri
         * @returns {Promise<object>} - CDR kayıtları
         */
        getCDRList: (filters) => {
            log("reports.getCDRList", filters);
            return request.post(`/webphone/redsantral/reports/cdr`, filters).then((response) => response.data);
        },

        /**
         * Arama kaydı dosyasının URL'ini getirir
         * @param {object} params - Kayıt dosyası parametreleri
         * @returns {Promise<object>} - Kayıt dosyası bilgileri
         */
        getRecordingFile: (params) => {
            log("reports.getRecordingFile", params);
            return request.post(`/webphone/redsantral/reports/recording`, params).then((response) => response.data);
        },

        /**
         * Visitor için CDR kayıtlarını getirir
         * @param {string} visitorId - Visitor ID
         * @param {object} filters - Filtreleme parametreleri
         * @returns {Promise<object>} - CDR kayıtları
         */
        getVisitorCDRList: (visitorId, filters) => {
            log("reports.getVisitorCDRList", { visitorId, filters });
            return request.post(`/webphone/redsantral/reports/visitor-cdr/${visitorId}`, filters).then((response) => response.data);
        },
    },

    calls: {
        /**
         * Arama başlatır
         * @param {object} callData - Arama parametreleri
         * @returns {Promise<object>} - Arama başlatma sonucu
         */
        make: (callData) => {
            log("calls.make", callData);
            return request.post(`/webphone/redsantral/calls/make`, callData).then((response) => response.data);
        },

        /**
         * Aramayı yönlendirir
         * @param {object} redirectData - Yönlendirme parametreleri
         * @returns {Promise<object>} - Yönlendirme sonucu
         */
        redirect: (redirectData) => {
            log("calls.redirect", redirectData);
            return request.post(`/webphone/redsantral/calls/redirect`, redirectData).then((response) => response.data);
        },

        /**
         * Aramayı sonlandırır
         * @param {string} uniqueid - Benzersiz arama ID'si
         * @returns {Promise<object>} - Sonlandırma sonucu
         */
        hangup: (uniqueid) => {
            log("calls.hangup", { uniqueid });
            return request.post(`/webphone/redsantral/calls/hangup`, { uniqueid }).then((response) => response.data);
        },

        /**
         * Arama dinleme (spy) başlatır
         * @param {object} spyData - Dinleme parametreleri
         * @returns {Promise<object>} - Dinleme başlatma sonucu
         */
        spy: (spyData) => {
            log("calls.spy", spyData);
            return request.post(`/webphone/redsantral/calls/spy`, spyData).then((response) => response.data);
        },

        /**
         * Anket araması başlatır
         * @param {object} surveyData - Anket parametreleri
         * @returns {Promise<object>} - Anket araması sonucu
         */
        survey: (surveyData) => {
            log("calls.survey", surveyData);
            return request.post(`/webphone/redsantral/calls/survey`, surveyData).then((response) => response.data);
        },
    },

    queues: {
        /**
         * Kuyrukları listeler
         * @param {string} queuenumber - Belirli bir kuyruk araması için (isteğe bağlı)
         * @returns {Promise<Array>} - Kuyruk listesi
         */
        list: (queuenumber = "") => {
            log("queues.list", { queuenumber });
            return request({
                url: `/webphone/redsantral/queues/list`,
                method: "get",
                params: { queuenumber },
            }).then((response) => response.data);
        },

        /**
         * Belirli bir kuyruğu getirir
         * @param {string} queuenumber - Kuyruk numarası
         * @returns {Promise<object>} - Kuyruk bilgileri
         */
        get: (queuenumber) => {
            log("queues.get", { queuenumber });
            return request({
                url: `/webphone/redsantral/queues/${queuenumber}`,
                method: "get",
            }).then((response) => response.data);
        },

        /**
         * Yeni kuyruk ekler
         * @param {object} queueData - Kuyruk bilgileri
         * @returns {Promise<object>} - Eklenen kuyruk bilgileri
         */
        create: (queueData) => {
            log("queues.create", queueData);
            return request.post(`/webphone/redsantral/queues`, queueData).then((response) => response.data);
        },

        /**
         * Kuyruk günceller
         * @param {object} queueData - Güncellenecek kuyruk bilgileri
         * @returns {Promise<object>} - Güncelleme sonucu
         */
        update: (queueData) => {
            log("queues.update", queueData);
            return request
                .patch(`/webphone/redsantral/queues`, queueData)
                .then((response) => response.data);
        },

        /**
         * Kuyruk siler
         * @param {string} queuenumber - Silinecek kuyruk numarası
         * @returns {Promise<object>} - Silme işlemi sonucu
         */
        delete: (queuenumber) => {
            log("queues.delete", { queuenumber });
            return request({
                url: `/webphone/redsantral/queues/${queuenumber}`,
                method: "delete",
            }).then((response) => response.data);
        },

        /**
         * Kuyruk canlı durumunu getirir
         * @param {string} queuenumber - Kontrol edilecek kuyruk numarası
         * @returns {Promise<object>} - Kuyruk canlı durumu
         */
        getLiveStatus: (queuenumber) => {
            log("queues.getLiveStatus", { queuenumber });
            return request({
                url: `/webphone/redsantral/queues/${queuenumber}/live-status`,
                method: "get",
            }).then((response) => response.data);
        },

        members: {
            /**
             * Kuyruk üyelerini (agentları) listeler
             * @param {string} queuenumber - Kuyruk numarası (boş gönderilirse tüm agentlar listelenir)
             * @param {string} agent - Belirli bir agent araması için (isteğe bağlı)
             * @returns {Promise<Array>} - Kuyruk üyeleri listesi
             */
            list: (queuenumber = "", agent = "") => {
                log("queues.members.list", { queuenumber, agent });
                return request({
                    url: `/webphone/redsantral/queues/members/list`,
                    method: "get",
                    params: { queuenumber, agent },
                }).then((response) => response.data);
            },

            /**
             * Kuyruğa yeni üye (agent) ekler
             * @param {object} memberData - Üye bilgileri
             * @returns {Promise<object>} - Eklenen üye bilgileri
             */
            add: (memberData) => {
                log("queues.members.add", memberData);
                return request.post(`/webphone/redsantral/queues/members`, memberData).then((response) => response.data);
            },

            /**
             * Kuyruktan üye (agent) siler
             * @param {string} memberid - Silinecek üye ID'si
             * @returns {Promise<object>} - Silme işlemi sonucu
             */
            delete: (memberid) => {
                log("queues.members.delete", { memberid });
                return request({
                    url: `/webphone/redsantral/queues/members/${memberid}`,
                    method: "delete",
                }).then((response) => response.data);
            },

            /**
             * Agent'ı mola (pause) durumuna alır
             * @param {object} pauseData - Mola parametreleri
             * @returns {Promise<object>} - Mola işlemi sonucu
             */
            pause: (pauseData) => {
                log("queues.members.pause", pauseData);
                return request
                    .patch(`/webphone/redsantral/queues/members/pause`, pauseData)
                    .then((response) => response.data);
            },
        },
    },
};
